// client/src/coms/components/VideoGridPanel.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useVoiceRoomPermissions } from '../usePermissions';
import { RoomEvent, ParticipantEvent, Track } from 'livekit-client';
import { describeShareError } from '../voip/shareErrors';
import BroadcastPanel, { BroadcastEndedNotice, LiveBroadcastBanner, stopBroadcast, useRoomBroadcasts } from './BroadcastPanel';

import {
  enableLocalCamera,
  disableLocalCamera,
  getLocalCameraOn,
  getLocalVideoMode,
  pickParticipantVideoTrack,
  setLocalVideoMode,
  getLocalVideoErr,
  ensureVideoQualityBindings,
  requestParticipantQuality,
  setLocalVideoQuality,
} from '../voip/videoClient';

import {
  initP2PScreenShare,
  startP2PScreenShare,
  stopP2PScreenShare,
  getP2PScreenEnabled,
  getP2PScreenErr,
  getP2PScreenRemoteStreams,
  getP2PScreenFps,
  setP2PScreenFps,
  getP2PScreenQuality,
  setP2PScreenQuality,
  setP2PScreenReceiveEnabled,
} from '../voip/p2pScreenShare';

import {
  initP2PCameraShare,
  startP2PCameraShare,
  stopP2PCameraShare,
  getP2PCameraErr,
  getP2PCameraRemoteStreams,
} from '../voip/p2pCameraShare';
import { buildScreenShareAudioPlaybackStream } from '../voip/screenShareAudioManager';

function safeUpper(s) {
  return String(s || '').trim().toUpperCase();
}

function initials(name) {
  const s = String(name || '').trim();
  if (!s) return 'U';
  const parts = s.split(/\s+/).filter(Boolean);
  const a = parts.slice(0, 2).map((p) => p[0]).join('');
  return safeUpper(a || s[0]);
}

function computeColumns(count, maxCols) {
  const n = Math.max(1, Number(count) || 0);
  const m = Math.max(1, Number(maxCols) || 0);
  if (n <= 1) return 1;
  if (n === 2) return 2;
  if (n <= 4) return Math.min(2, m);
  if (n <= 6) return Math.min(3, m);
  if (n <= 9) return Math.min(3, m);
  if (n <= 12) return Math.min(4, m);
  return Math.min(5, m);
}

function getFilterCss(filterId) {
  const id = String(filterId || 'none');
  if (id === 'soft') return 'contrast(1.02) saturate(1.05) brightness(1.04) blur(0.5px)';
  if (id === 'court') return 'contrast(1.14) saturate(0.85) brightness(1.08)';
  if (id === 'contrast') return 'contrast(1.22) saturate(1.0) brightness(1.05)';
  if (id === 'warm') return 'contrast(1.05) saturate(1.08) brightness(1.06) sepia(0.12)';
  if (id === 'cool') return 'contrast(1.06) saturate(1.04) brightness(1.06) hue-rotate(12deg)';
  if (id === 'grayscale') return 'grayscale(1) contrast(1.05) brightness(1.05)';
  if (id === 'blur') return 'blur(2px) contrast(1.02) saturate(1.02)';
  return 'none';
}

function safeObj(v) {
  return v && typeof v === 'object' ? v : {};
}

function getServerMemberDisplayName(server, identity) {
  const s = safeObj(server);
  const members = Array.isArray(s.members) ? s.members : [];
  const id = String(identity || '').trim();
  if (!id) return '';

  for (const m of members) {
    if (!m || typeof m !== 'object') continue;
    const mid = m.user_id != null ? String(m.user_id) : m.id != null ? String(m.id) : '';
    if (!mid) continue;
    if (mid !== id) continue;

    const dn = String(m.display_name || m.displayName || '').trim();
    if (dn) return dn;
    const un = String(m.username || m.name || '').trim();
    if (un) return un;
    return '';
  }

  return '';
}

function pickParticipantScreenTrack(participant) {
  if (!participant) return null;

  try {
    const pubs = Array.from(participant.videoTrackPublications?.values() || []);
    for (const pub of pubs) {
      if (!pub) continue;
      if (pub.isMuted) continue;

      let src = '';
      try {
        src = String(pub.source || (pub.track ? pub.track.source : '') || '');
      } catch {
        src = '';
      }

      const isScreen =
        src === String(Track.Source.ScreenShare) ||
        src === 'screen_share' ||
        src.toLowerCase().includes('screen');

      if (!isScreen) continue;

      const t = pub.track;
      if (!t) continue;

      try {
        if (t.kind && t.kind !== Track.Kind.Video) continue;
      } catch {}

      if (t.mediaStreamTrack) return t;
    }
  } catch {}

  return null;
}

function pickParticipantCameraTrack(participant) {
  if (!participant) return null;

  try {
    const pubs = Array.from(participant.videoTrackPublications?.values() || []);
    for (const pub of pubs) {
      if (!pub) continue;
      if (pub.isMuted) continue;

      let src = '';
      try {
        src = String(pub.source || (pub.track ? pub.track.source : '') || '');
      } catch {
        src = '';
      }

      const isCamera =
        src === String(Track.Source.Camera) ||
        src === 'camera' ||
        src.toLowerCase().includes('camera');

      if (!isCamera) continue;

      const t = pub.track;
      if (!t) continue;

      try {
        if (t.kind && t.kind !== Track.Kind.Video) continue;
      } catch {}

      if (t.mediaStreamTrack) return t;
    }
  } catch {}

  try {
    const t = pickParticipantVideoTrack(participant);
    if (t && t.mediaStreamTrack) return t;
  } catch {}

  return null;
}

function getLiveKitTileTrackMeta(participant) {
  const screenTrack = pickParticipantScreenTrack(participant);
  if (screenTrack) {
    return {
      livekitTrack: screenTrack,
      livekitKind: 'screen',
    };
  }

  const cameraTrack = pickParticipantCameraTrack(participant);
  if (cameraTrack) {
    return {
      livekitTrack: cameraTrack,
      livekitKind: 'camera',
    };
  }

  return {
    livekitTrack: null,
    livekitKind: null,
  };
}

function buildTiles(room, server) {
  const out = [];
  if (!room) return out;

  try {
    if (room.localParticipant) {
      const localScreen = room.__pa_p2p_local_screen_stream || null;
      const localCam = room.__pa_p2p_local_camera_stream || null;

      const p2pStream = localScreen || localCam || null;
      const p2pKind = localScreen ? 'screen' : localCam ? 'camera' : null;

      const lpId = String(room.localParticipant.identity || '').trim();
      const serverName = getServerMemberDisplayName(server, lpId);

      const livekitMeta = p2pStream ? { livekitTrack: null, livekitKind: null } : getLiveKitTileTrackMeta(room.localParticipant);

      out.push({
        key: 'local',
        local: true,
        name: serverName || room.localParticipant.name || room.localParticipant.identity || 'You',
        participant: room.localParticipant,
        p2pStream,
        p2pKind,
        livekitTrack: livekitMeta.livekitTrack,
        livekitKind: livekitMeta.livekitKind,
      });
    }
  } catch {}

  try {
    const p2pScreens = getP2PScreenRemoteStreams(room);
    const p2pCams = getP2PCameraRemoteStreams(room);

    room.remoteParticipants &&
      room.remoteParticipants.forEach((p) => {
        const id = String(p?.identity || '').trim();

        const screenStream = id && p2pScreens && p2pScreens[id] ? p2pScreens[id] : null;
        const camStream = id && p2pCams && p2pCams[id] ? p2pCams[id] : null;

        const p2pStream = screenStream || camStream || null;
        const p2pKind = screenStream ? 'screen' : camStream ? 'camera' : null;

        const livekitMeta = p2pStream ? { livekitTrack: null, livekitKind: null } : getLiveKitTileTrackMeta(p);

        out.push({
          key: p.sid || p.identity || String(Math.random()),
          local: false,
          name: p.name || getServerMemberDisplayName(server, p.identity) || p.identity || 'User',
          participant: p,
          p2pStream,
          p2pKind,
          livekitTrack: livekitMeta.livekitTrack,
          livekitKind: livekitMeta.livekitKind,
        });
      });
  } catch {}

  return out;
}

function Modal({ open, title, onClose, children }) {
  if (!open) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: 'rgba(0,0,0,0.72)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onMouseDown={(e) => {
        try {
          if (e.target === e.currentTarget) onClose && onClose();
        } catch {}
      }}
    >
      <div
        style={{
          width: 'min(520px, 100%)',
          borderRadius: 14,
          border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
          background: 'var(--vxs-1, #0f1012)',
          boxShadow: '0 20px 80px rgba(0,0,0,0.55)',
          padding: 16,
        }}
        onMouseDown={(e) => {
          try {
            e.stopPropagation();
          } catch {}
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <div style={{ fontWeight: 800, color: 'var(--vx-text, #fff)', fontSize: 16 }}>{title || 'Modal'}</div>
          <button type="button" className="coms-mini-btn" onClick={onClose}>
            Close
          </button>
        </div>

        <div style={{ height: 12 }} />

        {children}
      </div>
    </div>
  );
}

function clamp01(x) {
  const n = Number(x);
  if (!Number.isFinite(n)) return 1;
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}

function getAudioPrefsKey(identity) {
  const id = String(identity || '').trim();
  if (!id) return null;
  return `pa_coms_tile_audio_${id}`;
}

function loadAudioPrefs(identity) {
  const key = getAudioPrefsKey(identity);
  if (!key) return { volume: 1, muted: false };

  try {
    const raw = localStorage.getItem(key);
    if (!raw) return { volume: 1, muted: false };
    const obj = JSON.parse(raw);
    const vol = clamp01(obj && obj.volume);
    const muted = !!(obj && obj.muted);
    return { volume: vol, muted };
  } catch {
    return { volume: 1, muted: false };
  }
}

function saveAudioPrefs(identity, prefs) {
  const key = getAudioPrefsKey(identity);
  if (!key) return;

  try {
    const out = {
      volume: clamp01(prefs && prefs.volume),
      muted: !!(prefs && prefs.muted),
    };
    localStorage.setItem(key, JSON.stringify(out));
  } catch {}
}

function isScreenTileWithAudio(tile) {
  const t = tile || null;
  if (!t || t.local) return false;

  const isP2P = !!(t && t.p2pStream);
  const p2pKind = String(t?.p2pKind || '');
  if (isP2P && p2pKind === 'screen') return true;

  const livekitKind = String(t?.livekitKind || '');
  if (livekitKind !== 'screen') return false;

  return false;
}

function requestFullscreen(el) {
  const node = el || null;
  if (!node) return;

  try {
    const anyEl = node;
    if (anyEl.requestFullscreen) {
      anyEl.requestFullscreen().catch(() => {});
      return;
    }
    if (anyEl.webkitRequestFullscreen) {
      anyEl.webkitRequestFullscreen();
      return;
    }
    if (anyEl.mozRequestFullScreen) {
      anyEl.mozRequestFullScreen();
      return;
    }
    if (anyEl.msRequestFullscreen) {
      anyEl.msRequestFullscreen();
      return;
    }
  } catch {}
}

// Screen-share quality presets (targets, not guarantees: capture and the network decide the final result).
const SCREEN_QUALITY_PRESETS = [
  { id: 'performance', label: 'Performance', detail: '720p · 30 FPS', res: '720', fps: 30 },
  { id: 'balanced', label: 'Balanced', detail: '1080p · 30 FPS', res: '1080', fps: 30 },
  { id: 'quality', label: 'Quality', detail: '1080p · 60 FPS', res: '1080', fps: 60 },
];

function VideoTile({ tile, expanded, onToggleExpand, filterCss, cameraInsetEnabled, audioPref, onAudioPrefChange }) {
  const wrapRef = useRef(null);
  const vidRef = useRef(null);
  const audioRef = useRef(null);
  const insetRef = useRef(null);
  const [hasVideo, setHasVideo] = useState(false);
  const [hasInset, setHasInset] = useState(false);
  const [fitMode, setFitMode] = useState('');
  const [videoSize, setVideoSize] = useState('');

  useEffect(() => {
    const el = vidRef.current;
    const t = tile || null;
    const p = t && t.participant ? t.participant : null;
    if (!el || !p) return;

    const p2pStream = t && t.p2pStream ? t.p2pStream : null;
    if (p2pStream) return;

    // Remote tracks go through LiveKit's attach(): it registers the element with adaptive stream, so the
    // SFU sends a layer that fits the tile and pauses video for tiles that are hidden or off screen.
    let attachedTrack = null;

    const detach = () => {
      try {
        if (attachedTrack && typeof attachedTrack.detach === 'function') attachedTrack.detach(el);
      } catch {}
      attachedTrack = null;
      try {
        el.pause();
      } catch {}
      try {
        el.srcObject = null;
      } catch {}
      setHasVideo(false);
    };

    const attach = async () => {
      let videoTrack = null;
      const livekitKind = String(t?.livekitKind || '');

      if (livekitKind === 'screen') {
        videoTrack = pickParticipantScreenTrack(p);
      } else if (livekitKind === 'camera') {
        videoTrack = pickParticipantCameraTrack(p);
      } else {
        videoTrack = pickParticipantScreenTrack(p) || pickParticipantCameraTrack(p) || null;
      }

      if (!videoTrack || !videoTrack.mediaStreamTrack) {
        detach();
        return;
      }

      setHasVideo(true);

      if (attachedTrack && attachedTrack !== videoTrack) {
        try {
          attachedTrack.detach(el);
        } catch {}
        attachedTrack = null;
      }

      let viaLiveKit = false;
      if (!t.local && typeof videoTrack.attach === 'function') {
        try {
          videoTrack.attach(el);
          attachedTrack = videoTrack;
          viaLiveKit = true;
        } catch {}
      }
      if (!viaLiveKit) {
        try {
          const ms = new MediaStream();
          ms.addTrack(videoTrack.mediaStreamTrack);
          el.srcObject = ms;
        } catch {}
      }

      try {
        el.muted = true;
        el.volume = 0;
      } catch {}

      try {
        const playResult = el.play();
        if (playResult && typeof playResult.catch === 'function') await playResult.catch(() => {});
      } catch {}
    };

    attach();

    const off = [];

    const bindParticipant = (ev) => {
      const fn = () => {
        attach();
      };
      try {
        p.on(ev, fn);
        off.push(() => {
          try {
            p.off(ev, fn);
          } catch {}
        });
      } catch {}
    };

    bindParticipant(ParticipantEvent.TrackPublished);
    bindParticipant(ParticipantEvent.TrackUnpublished);
    bindParticipant(ParticipantEvent.TrackMuted);
    bindParticipant(ParticipantEvent.TrackUnmuted);

    return () => {
      try {
        off.forEach((f) => f());
      } catch {}
      detach();
    };
  }, [tile]);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;

    const t = tile || null;
    const shouldPlayAudio = isScreenTileWithAudio(t);

    let audioStream = null;

    const cleanup = () => {
      try {
        el.pause();
      } catch {}
      try {
        el.srcObject = null;
      } catch {}
    };

    const buildAudioStream = () => {
      if (!shouldPlayAudio) return null;

      const p2pStream = t && t.p2pStream ? t.p2pStream : null;
      const p = t && t.participant ? t.participant : null;

      return buildScreenShareAudioPlaybackStream({
        p2pStream,
        participant: p,
      });
    };

    const applyPlayback = async () => {
      cleanup();

      audioStream = buildAudioStream();
      if (!audioStream) return;

      try {
        el.srcObject = audioStream;
      } catch {
        return;
      }

      try {
        const vol = clamp01(audioPref?.volume);
        const muted = !!audioPref?.muted;
        el.muted = muted;
        el.volume = muted ? 0 : vol;
      } catch {}

      try {
        const playResult = el.play();
        if (playResult && typeof playResult.catch === 'function') await playResult.catch(() => {});
      } catch {}
    };

    applyPlayback();

    const p = t && t.participant ? t.participant : null;
    const off = [];
    const bindParticipant = (ev) => {
      if (!p) return;
      const fn = () => {
        applyPlayback();
      };
      try {
        p.on(ev, fn);
        off.push(() => {
          try {
            p.off(ev, fn);
          } catch {}
        });
      } catch {}
    };

    bindParticipant(ParticipantEvent.TrackPublished);
    bindParticipant(ParticipantEvent.TrackUnpublished);
    bindParticipant(ParticipantEvent.TrackMuted);
    bindParticipant(ParticipantEvent.TrackUnmuted);

    return () => {
      try {
        off.forEach((f) => f());
      } catch {}
      cleanup();
    };
  }, [tile, audioPref?.volume, audioPref?.muted]);

  useEffect(() => {
    const el = insetRef.current;
    if (!el) return;

    const t = tile || null;
    const isP2P = !!(t && t.p2pStream);
    const isP2PScreen = isP2P && String(t.p2pKind || '') === 'screen';
    const isLiveKitScreen = !isP2P && String(t?.livekitKind || '') === 'screen';

    if (!cameraInsetEnabled || (!isP2PScreen && !isLiveKitScreen)) {
      try {
        el.srcObject = null;
      } catch {}
      setHasInset(false);
      return;
    }

    const p = t && t.participant ? t.participant : null;
    if (!p) {
      try {
        el.srcObject = null;
      } catch {}
      setHasInset(false);
      return;
    }

    let attached = null;

    const detach = () => {
      try {
        if (attached && typeof attached.detach === 'function') attached.detach(el);
      } catch {}
      try {
        el.srcObject = null;
      } catch {}
      attached = null;
      setHasInset(false);
    };

    const attach = () => {
      const ct = pickParticipantCameraTrack(p);
      if (!ct) {
        detach();
        return;
      }

      attached = ct;
      setHasInset(true);

      try {
        if (typeof ct.attach === 'function') {
          ct.attach(el);
          return;
        }
      } catch {}

      try {
        const ms = new MediaStream();
        ms.addTrack(ct.mediaStreamTrack);
        el.srcObject = ms;
      } catch {}
    };

    attach();

    const off = [];
    const bindParticipant = (ev) => {
      const fn = () => {
        attach();
      };
      try {
        p.on(ev, fn);
        off.push(() => {
          try {
            p.off(ev, fn);
          } catch {}
        });
      } catch {}
    };

    bindParticipant(ParticipantEvent.TrackPublished);
    bindParticipant(ParticipantEvent.TrackUnpublished);
    bindParticipant(ParticipantEvent.TrackMuted);
    bindParticipant(ParticipantEvent.TrackUnmuted);

    return () => {
      try {
        off.forEach((f) => f());
      } catch {}
      detach();
    };
  }, [tile, cameraInsetEnabled]);

  useEffect(() => {
    const el = vidRef.current;
    if (!el) return;

    const t = tile || null;
    const p2pStream = t && t.p2pStream ? t.p2pStream : null;
    if (!p2pStream) return;

    let cancelled = false;

    const tryPlay = async () => {
      if (cancelled) return;
      try {
        el.muted = true;
        el.volume = 0;
        const p = el.play();
        if (p && typeof p.catch === 'function') await p.catch(() => {});
      } catch {}
    };

    const onLoadedMetadata = () => {
      tryPlay();
    };

    try {
      const ms = new MediaStream();
      const tracks = Array.from(p2pStream.getVideoTracks ? p2pStream.getVideoTracks() : []).filter(Boolean);
      for (const tr of tracks) {
        try {
          ms.addTrack(tr);
        } catch {}
      }
      el.srcObject = ms;
      setHasVideo(tracks.length > 0);
    } catch {
      setHasVideo(false);
    }

    try {
      el.addEventListener('loadedmetadata', onLoadedMetadata);
    } catch {}

    tryPlay();
    const tmr = setTimeout(() => tryPlay(), 250);

    return () => {
      cancelled = true;
      try {
        clearTimeout(tmr);
      } catch {}
      try {
        el.removeEventListener('loadedmetadata', onLoadedMetadata);
      } catch {}
      try {
        el.srcObject = null;
      } catch {}
    };
  }, [tile]);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;

    const t = tile || null;
    const shouldPlayAudio = isScreenTileWithAudio(t);
    if (!shouldPlayAudio) return;

    try {
      const vol = clamp01(audioPref?.volume);
      const muted = !!audioPref?.muted;
      el.muted = muted;
      el.volume = muted ? 0 : vol;
    } catch {}

    try {
      const p = el.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    } catch {}
  }, [tile, audioPref?.volume, audioPref?.muted]);

  const isP2P = !!(tile && tile.p2pStream);
  const p2pKind = String(tile?.p2pKind || '');
  const livekitKind = String(tile?.livekitKind || '');
  const screenWithAudio = isScreenTileWithAudio(tile);

  const volUi = clamp01(audioPref?.volume);
  const mutedUi = !!audioPref?.muted;

  const tileKind = isP2P ? p2pKind : livekitKind;
  const isScreenTile = String(tileKind || '') === 'screen';
  const isExpandedScreen = !!expanded && isScreenTile;
  // Screens default to the whole picture when expanded (text stays readable); viewers can switch.
  const mainObjectFit = fitMode || (isExpandedScreen ? 'contain' : 'cover');
  const tileLabel = tile.local ? (isScreenTile ? 'Your screen' : 'You') : isScreenTile ? `${tile.name} · screen` : tile.name;

  const onVideoResize = (e) => {
    const v = e && e.currentTarget;
    const w = Number(v && v.videoWidth) || 0;
    const h = Number(v && v.videoHeight) || 0;
    setVideoSize(w && h ? `${w}×${h}` : '');
  };

  return (
    <div
      ref={wrapRef}
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        borderRadius: 12,
        overflow: 'hidden',
        border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.10 * var(--vx-ov, 1)))',
        background: 'var(--vxs-1, #0f1012)',
      }}
    >
      <div style={{ position: 'absolute', left: 10, top: 10, zIndex: 7, display: 'flex', gap: 8 }}>
        <button
          type="button"
          className="coms-mini-btn"
          style={{
            padding: '6px 10px',
            borderRadius: 10,
            background: 'rgba(0,0,0,0.55)',
            border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
            color: 'var(--vx-text, #fff)',
          }}
          onClick={(e) => {
            try {
              e.preventDefault();
              e.stopPropagation();
            } catch {}
            try {
              onToggleExpand && onToggleExpand(tile);
            } catch {}
          }}
          title={expanded ? 'Collapse' : 'Expand'}
        >
          {expanded ? 'Collapse' : 'Expand'}
        </button>

        <button
          type="button"
          className="coms-mini-btn"
          style={{
            padding: '6px 10px',
            borderRadius: 10,
            background: 'rgba(0,0,0,0.55)',
            border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
            color: 'var(--vx-text, #fff)',
          }}
          onClick={(e) => {
            try {
              e.preventDefault();
              e.stopPropagation();
            } catch {}
            try {
              requestFullscreen(wrapRef.current || vidRef.current);
            } catch {}
          }}
          title="Fullscreen"
        >
          Fullscreen
        </button>

        {hasVideo && (expanded || isScreenTile) ? (
          <button
            type="button"
            className="coms-mini-btn"
            style={{
              padding: '6px 10px',
              borderRadius: 10,
              background: 'rgba(0,0,0,0.55)',
              border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
              color: 'var(--vx-text, #fff)',
            }}
            onClick={(e) => {
              try {
                e.preventDefault();
                e.stopPropagation();
              } catch {}
              setFitMode(mainObjectFit === 'contain' ? 'cover' : 'contain');
            }}
            title={mainObjectFit === 'contain' ? 'Fill the tile (crops the edges)' : 'Fit the whole picture in the tile'}
            aria-pressed={mainObjectFit === 'contain'}
          >
            {mainObjectFit === 'contain' ? 'Fill' : 'Fit'}
          </button>
        ) : null}
      </div>

      <video
        ref={vidRef}
        autoPlay
        playsInline
        muted
        onResize={onVideoResize}
        onLoadedMetadata={onVideoResize}
        aria-label={tileLabel}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: mainObjectFit,
          objectPosition: 'center center',
          transform: tile.local && !isP2P ? 'scaleX(-1)' : 'none',
          filter: filterCss,
          background: '#000',
        }}
      />

      <audio ref={audioRef} autoPlay playsInline style={{ display: 'none' }} />

      {hasInset ? (
        <video
          ref={insetRef}
          autoPlay
          playsInline
          muted
          style={{
            position: 'absolute',
            right: 12,
            bottom: 12,
            width: '26%',
            aspectRatio: '16 / 9',
            borderRadius: 12,
            overflow: 'hidden',
            border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.16 * var(--vx-ov, 1)))',
            boxShadow: '0 10px 30px rgba(0,0,0,0.55)',
            objectFit: 'cover',
            background: 'var(--vxs-1, #0f1012)',
            transform: tile.local ? 'scaleX(-1)' : 'none',
          }}
        />
      ) : (
        <video ref={insetRef} style={{ display: 'none' }} />
      )}

      {!hasVideo ? (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(180deg, rgba(var(--vx-fg-rgb, 255,255,255), calc(0.04 * var(--vx-ov, 1))), rgba(var(--vx-fg-rgb, 255,255,255), calc(0.01 * var(--vx-ov, 1))))',
          }}
        >
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: 999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 800,
              letterSpacing: 1,
              color: 'var(--vx-text, #fff)',
              border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.14 * var(--vx-ov, 1)))',
              background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.06 * var(--vx-ov, 1)))',
            }}
          >
            {initials(tile.name)}
          </div>
        </div>
      ) : null}

      <div
        style={{
          position: 'absolute',
          left: 10,
          bottom: 10,
          padding: '6px 8px',
          borderRadius: 10,
          fontSize: 13,
          lineHeight: '13px',
          color: 'var(--vx-text, #fff)',
          background: 'rgba(0,0,0,0.55)',
          border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.10 * var(--vx-ov, 1)))',
          maxWidth: 'calc(100% - 20px)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          zIndex: 6,
        }}
        title={videoSize ? `${tileLabel} (receiving ${videoSize})` : tileLabel}
      >
        {tileLabel}
        {hasVideo && videoSize && (expanded || isScreenTile) ? (
          <span style={{ marginLeft: 6, opacity: 0.7, fontWeight: 600 }}>{videoSize}</span>
        ) : null}
      </div>

      {isP2P ? (
        <div
          style={{
            position: 'absolute',
            right: 10,
            top: 10,
            padding: '5px 8px',
            borderRadius: 10,
            fontSize: 12,
            lineHeight: '12px',
            fontWeight: 800,
            letterSpacing: 0.4,
            color: 'var(--vx-text, #fff)',
            background: 'rgba(0,0,0,0.55)',
            border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
            zIndex: 7,
          }}
          title={p2pKind === 'camera' ? 'Direct (P2P) camera' : 'Direct (P2P) screenshare'}
        >
          P2P
        </div>
      ) : livekitKind ? (
        <div
          style={{
            position: 'absolute',
            right: 10,
            top: 10,
            padding: '5px 8px',
            borderRadius: 10,
            fontSize: 12,
            lineHeight: '12px',
            fontWeight: 800,
            letterSpacing: 0.4,
            color: 'var(--vx-text, #fff)',
            background: 'rgba(0,0,0,0.55)',
            border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
            zIndex: 7,
          }}
          title={livekitKind === 'camera' ? 'LiveKit camera' : 'LiveKit screenshare'}
        >
          SFU
        </div>
      ) : null}

      {screenWithAudio ? (
        <div
          style={{
            position: 'absolute',
            right: 10,
            bottom: 10,
            zIndex: 7,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '7px 8px',
            borderRadius: 12,
            background: 'rgba(0,0,0,0.55)',
            border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
            maxWidth: 'calc(100% - 20px)',
          }}
          onMouseDown={(e) => {
            try {
              e.stopPropagation();
            } catch {}
          }}
          onClick={(e) => {
            try {
              e.stopPropagation();
            } catch {}
          }}
        >
          <button
            type="button"
            className="coms-mini-btn"
            style={{
              padding: '6px 10px',
              borderRadius: 10,
              background: 'rgba(0,0,0,0.35)',
              border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
              color: 'var(--vx-text, #fff)',
              fontWeight: 800,
            }}
            onMouseDown={(e) => {
              try {
                e.stopPropagation();
              } catch {}
            }}
            onClick={(e) => {
              try {
                e.stopPropagation();
              } catch {}
              try {
                onAudioPrefChange && onAudioPrefChange({ muted: !mutedUi });
              } catch {}
            }}
            title={mutedUi ? 'Unmute' : 'Mute'}
          >
            {mutedUi ? 'Unmute' : 'Mute'}
          </button>

          <input
            type="range"
            min={0}
            max={100}
            step={1}
            value={Math.round(volUi * 100)}
            onMouseDown={(e) => {
              try {
                e.stopPropagation();
              } catch {}
            }}
            onClick={(e) => {
              try {
                e.stopPropagation();
              } catch {}
            }}
            onChange={(e) => {
              const v = clamp01(Number(e.target.value) / 100);
              try {
                onAudioPrefChange && onAudioPrefChange({ volume: v, muted: v <= 0 ? true : mutedUi });
              } catch {}
            }}
            style={{
              width: 140,
              accentColor: '#fff',
            }}
            title={`Volume: ${Math.round(volUi * 100)}%`}
          />

          <div
            style={{
              minWidth: 34,
              textAlign: 'right',
              fontSize: 12,
              color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.9 * var(--vx-ov, 1)))',
              fontWeight: 800,
            }}
          >
            {mutedUi ? '0%' : `${Math.round(volUi * 100)}%`}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function VideoGridPanel({ room, server, serverKey, hideCameraButton }) {
  const [tiles, setTiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [camOn, setCamOn] = useState(false);
  const [p2pOn, setP2pOn] = useState(false);
  const [err, setErr] = useState('');
  const [filterId, setFilterId] = useState('none');
  const [expandedKey, setExpandedKey] = useState(null);
  const lastExpandedIdentityRef = useRef('');

  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const { broadcasts, refresh: refreshBroadcasts, endedNotice, dismissEnded } = useRoomBroadcasts(room);
  const myIdentity = String((room && room.localParticipant && room.localParticipant.identity) || '');
  const nameForUserId = (uid) => {
    try {
      const p = room && room.remoteParticipants ? room.remoteParticipants.get(String(uid)) : null;
      return p ? String(p.name || p.identity || '') : '';
    } catch {
      return '';
    }
  };
  const myBroadcastLive = broadcasts.some((b) => String(b.user_id) === myIdentity);
  // Go live needs Start broadcasts in this voice channel (the server checks it again).
  const roomVoicePerms = useVoiceRoomPermissions(room && room.name);
  const canStartBroadcast = roomVoicePerms.has('start_broadcast');

  // Ending the screen share ends the external broadcast too (it would otherwise keep running blank).
  const wasSharingRef = useRef(false);
  useEffect(() => {
    if (p2pOn) {
      wasSharingRef.current = true;
      return;
    }
    // Only react to the share actually ending in this view, not to the initial "not yet known" state.
    if (!wasSharingRef.current || !myBroadcastLive) return;
    wasSharingRef.current = false;
    const mine = broadcasts.filter((b) => String(b.user_id) === myIdentity);
    Promise.all(mine.map((b) => stopBroadcast(b.id).catch(() => null))).then(() => refreshBroadcasts());
  }, [p2pOn, myBroadcastLive, broadcasts, myIdentity, refreshBroadcasts]);

  const [audioPrefsByIdentity, setAudioPrefsByIdentity] = useState({});
  const [screenFps, setScreenFps] = useState(30);
  const [screenRes, setScreenRes] = useState('720');

  function refreshAll() {
    if (!room) {
      setTiles([]);
      setCamOn(false);
      setP2pOn(false);
      setExpandedKey(null);
      setScreenFps(20);
      setScreenRes('720');
      return;
    }

    setTiles(buildTiles(room, server));

    try {
      setCamOn(getLocalCameraOn(room));
    } catch {
      setCamOn(false);
    }

    try {
      setP2pOn(getP2PScreenEnabled(room));
    } catch {
      setP2pOn(false);
    }

    try {
      const f = Number(getP2PScreenFps(room));
      setScreenFps(f === 60 ? 60 : f === 30 ? 30 : 20);
    } catch {
      setScreenFps(20);
    }

    try {
      const q = String(getP2PScreenQuality(room) || '720');
      setScreenRes(q === '1080' ? '1080' : '720');
    } catch {
      setScreenRes('720');
    }
  }

  useEffect(() => {
    refreshAll();
    try {
      ensureVideoQualityBindings(room);
    } catch {}
    try {
      setLocalVideoQuality(room, 'thumb');
    } catch {}
    try {
      initP2PScreenShare(room);
    } catch {}
    try {
      initP2PCameraShare(room);
    } catch {}
    try {
      setP2PScreenReceiveEnabled(room, true);
    } catch {}

    return () => {
      try {
        setP2PScreenReceiveEnabled(room, false);
      } catch {}
    };
  }, [room]);

  useEffect(() => {
    if (!expandedKey) return;
    try {
      const ok = tiles.some((t) => String(t.key) === String(expandedKey));
      if (!ok) setExpandedKey(null);
    } catch {}
  }, [expandedKey, tiles]);

  useEffect(() => {
    if (!room) return;

    const refresh = () => refreshAll();

    const off = [];

    const bind = (ev) => {
      try {
        room.on(ev, refresh);
        off.push(() => {
          try {
            room.off(ev, refresh);
          } catch {}
        });
      } catch {}
    };

    bind(RoomEvent.ParticipantConnected);
    bind(RoomEvent.ParticipantDisconnected);
    bind(RoomEvent.TrackSubscribed);
    bind(RoomEvent.TrackUnsubscribed);
    bind(RoomEvent.LocalTrackPublished);
    bind(RoomEvent.LocalTrackUnpublished);
    bind(RoomEvent.TrackMuted);
    bind(RoomEvent.TrackUnmuted);

    const onP2PUpdated = () => {
      try {
        refresh();
      } catch {}
    };

    try {
      window.addEventListener('pa-p2p-screens-updated', onP2PUpdated, { passive: true });
      off.push(() => {
        try {
          window.removeEventListener('pa-p2p-screens-updated', onP2PUpdated);
        } catch {}
      });
    } catch {}

    try {
      window.addEventListener('pa-p2p-cams-updated', onP2PUpdated, { passive: true });
      off.push(() => {
        try {
          window.removeEventListener('pa-p2p-cams-updated', onP2PUpdated);
        } catch {}
      });
    } catch {}

    refresh();

    return () => {
      try {
        off.forEach((f) => f());
      } catch {}
    };
  }, [room]);

  useEffect(() => {
    const list = Array.isArray(tiles) ? tiles : [];
    if (list.length === 0) return;

    const next = {};
    try {
      for (const t of list) {
        if (!t || t.local) continue;
        const id = String(t?.participant?.identity || '').trim();
        if (!id) continue;
        next[id] = loadAudioPrefs(id);
      }
    } catch {}

    setAudioPrefsByIdentity((prev) => {
      const out = { ...(prev || {}) };
      for (const k of Object.keys(next)) {
        if (!out[k]) out[k] = next[k];
      }
      return out;
    });
  }, [tiles]);

  function updateTileAudioPref(identity, patch) {
    const id = String(identity || '').trim();
    if (!id) return;

    setAudioPrefsByIdentity((prev) => {
      const cur = prev && prev[id] ? prev[id] : loadAudioPrefs(id);
      const next = {
        volume: clamp01(patch && patch.volume != null ? patch.volume : cur.volume),
        muted: patch && patch.muted != null ? !!patch.muted : !!cur.muted,
      };
      saveAudioPrefs(id, next);
      return { ...(prev || {}), [id]: next };
    });
  }

  async function toggleCamera() {
    if (!room || !room.localParticipant) return;
    if (busy) return;

    setBusy(true);
    setErr('');

    try {
      const on = !!getLocalCameraOn(room);

      if (on) {
        const r = await disableLocalCamera(room);
        if (!r || !r.ok) throw new Error((r && r.reason) || getP2PCameraErr(room) || 'camera_disable_failed');
      } else {
        const maxPeers = 4;
        const r = await enableLocalCamera(room, { maxPeers });
        if (!r || !r.ok) throw new Error((r && r.reason) || getP2PCameraErr(room) || 'camera_enable_failed');
      }

      refreshAll();
    } catch (e) {
      setErr(describeShareError(String((e && e.message) || e), 'camera'));
    } finally {
      setBusy(false);
    }
  }

  async function stopAnyScreenShare() {
    if (!room || !room.localParticipant) return;
    if (busy) return;

    setBusy(true);
    setErr('');

    try {
      const p2p = !!getP2PScreenEnabled(room);
      if (p2p) await stopP2PScreenShare(room);

      try {
        const mode = String(getLocalVideoMode(room) || 'none');
        if (mode === 'screen') await setLocalVideoMode(room, 'none');
        else if (mode === 'both') await setLocalVideoMode(room, 'camera');
      } catch {}

      refreshAll();
    } catch (e) {
      const why = String((e && e.message) || e || 'stop_screen_failed');
      const p2pExtra = String(getP2PScreenErr(room) || '').trim();
      const sfuExtra = String(getLocalVideoErr(room) || '').trim();
      const extra = p2pExtra || sfuExtra;
      setErr(extra ? `${why} (${extra})` : why);
      refreshAll();
    } finally {
      setBusy(false);
    }
  }

  async function startShareFromModal() {
    if (!room || !room.localParticipant) return;
    if (busy) return;

    setBusy(true);
    setErr('');

    try {
      setShareModalOpen(false);

      try {
        await stopP2PScreenShare(room);
      } catch {}

      try {
        const mode = String(getLocalVideoMode(room) || 'none');
        if (mode === 'screen') await setLocalVideoMode(room, 'none');
        if (mode === 'both') await setLocalVideoMode(room, 'camera');
      } catch {}

      try {
        await setP2PScreenQuality(room, screenRes === '1080' ? '1080' : '720');
      } catch {}

      try {
        await setP2PScreenFps(room, screenFps);
      } catch {}

      const maxPeers = 4;
      const r = await startP2PScreenShare(room, { maxPeers });
      if (!r || !r.ok) throw new Error(String((r && r.reason) || getP2PScreenErr(room) || 'p2p_screen_failed'));

      refreshAll();
    } catch (e) {
      const why = String((e && e.message) || e || 'start_screen_failed');
      setErr(describeShareError(why, 'screen'));
      refreshAll();
    } finally {
      setBusy(false);
    }
  }

  function onClickShareScreen() {
    if (!room || !room.localParticipant) return;
    if (busy) return;

    const isP2P = !!p2pOn;

    if (isP2P) {
      stopAnyScreenShare();
      return;
    }

    setShareModalOpen(true);
  }

  const cols = useMemo(() => computeColumns(tiles.length, 6), [tiles.length]);
  const filterCss = useMemo(() => getFilterCss(filterId), [filterId]);

  const sharingNow = !!p2pOn;
  const shareLabel = sharingNow ? 'Stop screen' : 'Share screen';

  const cameraInsetEnabled = true;

  const expandedTile = useMemo(() => {
    if (!expandedKey) return null;
    const k = String(expandedKey);
    return tiles.find((t) => String(t.key) === k) || null;
  }, [expandedKey, tiles]);

  useEffect(() => {
    if (!room || !room.localParticipant) return;

    const prev = String(lastExpandedIdentityRef.current || '').trim();
    const next = String(expandedTile?.participant?.identity || '').trim();

    if (prev && prev !== next) {
      try {
        const p = room.remoteParticipants?.get ? room.remoteParticipants.get(prev) : null;
        if (p) requestParticipantQuality(room, p, 'thumb');
      } catch {}
    }

    if (next) {
      try {
        requestParticipantQuality(room, expandedTile.participant, 'expanded');
      } catch {}
    }

    lastExpandedIdentityRef.current = next || '';
  }, [room, expandedTile]);

  const smallTiles = useMemo(() => {
    if (!expandedKey) return tiles;
    const k = String(expandedKey);
    return tiles.filter((t) => String(t.key) !== k);
  }, [expandedKey, tiles]);

  function toggleExpandFor(tile) {
    const t = tile || null;
    const k = t ? String(t.key || '') : '';
    if (!k) return;

    setExpandedKey((prev) => {
      const p = prev ? String(prev) : '';
      return p === k ? null : k;
    });
  }

  async function onChangeScreenFps(nextFps) {
    if (!room || !room.localParticipant) return;
    if (busy) return;

    const n = Number(nextFps);
    const v = n === 60 ? 60 : n === 30 ? 30 : 20;

    setBusy(true);
    setErr('');

    try {
      setScreenFps(v);

      if (p2pOn) {
        const r = await setP2PScreenFps(room, v);
        if (!r || !r.ok) throw new Error(String((r && r.reason) || getP2PScreenErr(room) || 'p2p_fps_failed'));
      }

      refreshAll();
    } catch (e) {
      const why = String((e && e.message) || e || 'set_fps_failed');
      const p2pExtra = String(getP2PScreenErr(room) || '').trim();
      setErr(p2pExtra ? `${why} (${p2pExtra})` : why);
      refreshAll();
    } finally {
      setBusy(false);
    }
  }

  async function onChangeScreenRes(nextRes) {
    if (!room || !room.localParticipant) return;
    if (busy) return;

    const v = String(nextRes) === '1080' ? '1080' : '720';

    setBusy(true);
    setErr('');

    try {
      setScreenRes(v);

      if (p2pOn) {
        const r = await setP2PScreenQuality(room, v);
        if (!r || !r.ok) throw new Error(String((r && r.reason) || getP2PScreenErr(room) || 'p2p_res_failed'));
      }

      refreshAll();
    } catch (e) {
      const why = String((e && e.message) || e || 'set_res_failed');
      const p2pExtra = String(getP2PScreenErr(room) || '').trim();
      setErr(p2pExtra ? `${why} (${p2pExtra})` : why);
      refreshAll();
    } finally {
      setBusy(false);
    }
  }

  const showP2PControls = !!sharingNow && !!p2pOn;

  return (
    <div style={{ marginTop: 14 }}>
      <Modal
        open={shareModalOpen}
        title="Share screen"
        onClose={() => {
          if (busy) return;
          setShareModalOpen(false);
        }}
      >
        <div role="radiogroup" aria-label="Stream quality" style={{ display: 'grid', gap: 8 }}>
          {SCREEN_QUALITY_PRESETS.map((p) => {
            const on = screenRes === p.res && Number(screenFps) === p.fps;
            return (
              <button
                key={p.id}
                type="button"
                role="radio"
                aria-checked={on}
                className={`coms-mini-btn${on ? ' primary' : ''}`}
                disabled={busy}
                onClick={() => {
                  setScreenRes(p.res);
                  setScreenFps(p.fps);
                }}
                style={{ display: 'flex', justifyContent: 'space-between', gap: 12, textAlign: 'left', padding: '10px 12px' }}
              >
                <span style={{ fontWeight: 800 }}>{p.label}</span>
                <span style={{ opacity: 0.8 }}>{p.detail}</span>
              </button>
            );
          })}
        </div>

        <div style={{ height: 12 }} />

        <div style={{ color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.78 * var(--vx-ov, 1)))', fontSize: 13, lineHeight: '18px' }}>
          Your browser asks what to share: a whole screen, a window or a tab. Tab and system audio are offered where the
          browser supports them. Quality is a target: it adapts to your connection and to what your device can capture.
          The stream goes directly to up to four viewers (direct connections can reveal your IP address to them) and
          through the voice server when more people watch or the direct connection fails.
        </div>

        <div style={{ height: 14 }} />

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button type="button" className="coms-mini-btn" disabled={busy} onClick={() => setShareModalOpen(false)}>
            Cancel
          </button>
          <button type="button" className="coms-mini-btn primary" disabled={busy} onClick={startShareFromModal}>
            Start
          </button>
        </div>
      </Modal>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontWeight: 700 }}>Video</div>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            value={filterId}
            onChange={(e) => setFilterId(String(e.target.value || 'none'))}
            style={{
              background: 'var(--vxs-1, #111214)',
              color: 'var(--vx-text, #fff)',
              border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
              borderRadius: 8,
              padding: '8px 10px',
              minWidth: 160,
            }}
          >
            <option value="none">No filter</option>
            <option value="soft">Soft</option>
            <option value="court">Courtroom</option>
            <option value="contrast">Contrast</option>
            <option value="warm">Warm</option>
            <option value="cool">Cool</option>
            <option value="grayscale">Grayscale</option>
            <option value="blur">Blur</option>
          </select>

          {hideCameraButton ? null : (
            <button type="button" disabled={!room || busy} className="coms-mini-btn primary" onClick={toggleCamera}>
              {camOn ? 'Stop camera' : 'Start camera'}
            </button>
          )}

          <button type="button" disabled={!room || busy} className="coms-mini-btn" onClick={onClickShareScreen}>
            {shareLabel}
          </button>

          {p2pOn && !myBroadcastLive && canStartBroadcast ? (
            <button type="button" disabled={!room || busy} className="coms-mini-btn" onClick={() => setBroadcastOpen(true)} title="Broadcast your screen to YouTube, Twitch or RTMP">
              Go live…
            </button>
          ) : null}

          {showP2PControls ? (
            <select
              value={screenRes}
              onChange={(e) => onChangeScreenRes(String(e.target.value))}
              disabled={!room || busy || !p2pOn}
              style={{
                background: 'var(--vxs-1, #111214)',
                color: 'var(--vx-text, #fff)',
                border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
                borderRadius: 8,
                padding: '8px 10px',
                minWidth: 120,
              }}
              title="Screenshare resolution (P2P send)"
            >
              <option value="720">720p</option>
              <option value="1080">1080p</option>
            </select>
          ) : null}

          {showP2PControls ? (
            <select
              value={screenFps}
              onChange={(e) => onChangeScreenFps(Number(e.target.value))}
              disabled={!room || busy || !p2pOn}
              style={{
                background: 'var(--vxs-1, #111214)',
                color: 'var(--vx-text, #fff)',
                border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
                borderRadius: 8,
                padding: '8px 10px',
                minWidth: 110,
              }}
              title="Screenshare FPS (P2P)"
            >
              <option value={20}>20 FPS</option>
              <option value={30}>30 FPS</option>
              <option value={60}>60 FPS</option>
            </select>
          ) : null}
        </div>
      </div>

      {err ? (
        <div className="coms-form-error" style={{ marginTop: 10 }}>
          {err}
        </div>
      ) : null}

      <BroadcastEndedNotice notice={endedNotice} onDismiss={dismissEnded} />

      <LiveBroadcastBanner
        broadcasts={broadcasts}
        nameForUserId={nameForUserId}
        myUserId={myIdentity}
        onStop={async (id) => {
          try {
            await stopBroadcast(id);
          } catch (e) {
            setErr(String((e && e.data && e.data.error) || 'Could not stop the broadcast.'));
          }
          refreshBroadcasts();
        }}
      />

      <BroadcastPanel
        room={room}
        open={broadcastOpen}
        onClose={() => setBroadcastOpen(false)}
        screenSharing={!!p2pOn}
        onStarted={() => refreshBroadcasts()}
      />

      {expandedTile ? (
        <div
          style={{
            marginTop: 10,
            width: '100%',
            borderRadius: 14,
            overflow: 'hidden',
            border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.10 * var(--vx-ov, 1)))',
            background: 'var(--vxs-1, #0f1012)',
            boxShadow: '0 16px 60px rgba(0,0,0,0.35)',
          }}
        >
          <div
            style={{
              width: '100%',
              aspectRatio: '16 / 9',
              maxHeight: '70vh',
            }}
          >
            {(() => {
              const id = String(expandedTile?.participant?.identity || '').trim();
              const pref = id ? audioPrefsByIdentity[id] || loadAudioPrefs(id) : { volume: 1, muted: false };

              return (
                <VideoTile
                  tile={expandedTile}
                  expanded
                  onToggleExpand={toggleExpandFor}
                  filterCss={filterCss}
                  cameraInsetEnabled={cameraInsetEnabled}
                  audioPref={pref}
                  onAudioPrefChange={(patch) => {
                    updateTileAudioPref(id, patch);
                  }}
                />
              );
            })()}
          </div>
        </div>
      ) : null}

      <div
        style={{
          marginTop: 10,
          display: 'grid',
          gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
          gap: 10,
          width: '100%',
        }}
      >
        {smallTiles.map((t) => {
          const id = String(t?.participant?.identity || '').trim();
          const pref = id ? audioPrefsByIdentity[id] || loadAudioPrefs(id) : { volume: 1, muted: false };

          return (
            <div
              key={t.key}
              style={{
                width: '100%',
                aspectRatio: '16 / 9',
                minHeight: 120,
              }}
            >
              <VideoTile
                tile={t}
                expanded={String(expandedKey || '') === String(t.key)}
                onToggleExpand={toggleExpandFor}
                filterCss={filterCss}
                cameraInsetEnabled={cameraInsetEnabled}
                audioPref={pref}
                onAudioPrefChange={(patch) => {
                  updateTileAudioPref(id, patch);
                }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}